import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Eye, Wind, Anchor, Calendar, CheckCircle2, X, MessageCircle, ZoomIn,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import QMGSymbols from '../components/QMGSymbols';

const RESIST = {
  label: 'Non-Acceptance',
  accent: '#8B1E1E',
  bg: 'bg-[#FDF3F1]',
  border: 'border-[#8B1E1E]/30',
};
const ACCEPT = {
  label: 'Acceptance',
  accent: '#2C5530',
  bg: 'bg-[#F1F6F2]',
  border: 'border-[#2C5530]/30',
};

// Image asset URLs — full set uploaded by user
const IMG = {
  firstTeeR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/b1uqsrj5_First%20tee%20-%20non-acceptance.png',
  firstTeeA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/xtrw4ejk_First%20tee%20-%20acceptance.png',
  approachR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/9tf4vw7n_non-acceptance.png',
  approachA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/gmvbwyjo_acceptance.png',
  shortGameR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/hf69qf82_short%20game%20-%20non-acceptance.png',
  shortGameA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/tk5itzci_short%20game%20-%20acceptance.png',
  puttingR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/ank894qs_putting%20-%20non-acceptance.png',
  puttingA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/oj4o0szr_putting%20-%20acceptance.png',
  finalR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/x93yrvka_last%20hole%20-%20non-acceptance.png',
  finalA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/4v9bw5d0_Final%20Hole%20-%20acceptance.png',
  reflectionR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/sqhtz5j1_Post%20round%20-%20non%20accaptance.png',
  reflectionA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/tihcpwnd_Post%20round%20accepting%20golfer.png',
  trainingR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/2npimzy9_training%20non-acceptance.png',
  trainingA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/ee02aai0_training%20acceptance.png',
  awayR: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/q7jxn8zt_Away%20from%20golf%20non-acceptance.png',
  awayA: 'https://customer-assets.emergentagent.com/job_031288f4-3b8c-47a9-90f7-5e08ae8888cd/artifacts/94bdz0lx_Away%20for%20golf%20-%20acceptance.png',
};

const SCENARIOS = [
  {
    key: 'training',
    label: 'Training',
    short: 'Practice',
    states: {
      resistance: {
        image: IMG.trainingR,
        sub: 'Hunting the Fix',
        headline: 'Practice as a hunt for the fix that never comes.',
        body: 'The range becomes a courtroom — every shot is judged, every miss is evidence. You hunt drills, swap coaches, second-guess your routine, all in search of the swing that finally feels safe. Practice without acceptance is anxiety with a club in your hand.',
        markers: [
          'Hitting balls to silence doubt instead of build skill',
          'Chasing the next drill, the next tip, the next coach',
          'Treating every miss as proof you are broken',
          'Leaving the range tired but no clearer',
        ],
      },
      acceptance: {
        image: IMG.trainingA,
        sub: 'Learn, Don\'t Fear',
        headline: 'Practice as learning — not as proof.',
        body: 'Acceptance on the range means turning every ball into a curiosity, not a verdict. You set an intention, train one skill at a time, notice what your mind is doing, and walk off having grown — regardless of how the strikes felt.',
        markers: [
          'Setting one clear intention per session',
          'Training attention as deliberately as technique',
          'Naming thoughts without obeying them',
          'Measuring practice by learning, not strike quality',
        ],
      },
    },
  },
  {
    key: 'first-tee',
    label: 'First Tee',
    short: 'Nerves',
    states: {
      resistance: {
        image: IMG.firstTeeR,
        sub: 'The Pre-Shot Spiral',
        headline: 'Standing on the first tee, your mind plays the round before the ball is hit.',
        body: 'Doubt floods in before the swing — worst-case scenarios, fear of judgement, the pressure of how the round "should" start. The body tightens, the routine speeds up, and the swing becomes a fight rather than a free movement.',
        markers: [
          'Replaying every possible miss before the ball is teed',
          'Trying to suppress nerves — which only amplifies them',
          'Speeding through the routine to escape the discomfort',
          'Hoping for a perfect start instead of committing to a process',
        ],
      },
      acceptance: {
        image: IMG.firstTeeA,
        sub: 'Nerves Welcome',
        headline: "First-tee nerves are normal — and they're useful.",
        body: 'You don\'t need to feel calm to play well. You need to be willing to feel nervous AND still commit to your routine. Acceptance lets the energy of the moment fuel focus instead of hijack it.',
        markers: [
          'Naming the feeling — "I am nervous, that is fine"',
          'Letting the heart rate be what it is',
          'Walking through the same routine you trust',
          'Choosing one clear target and committing fully',
        ],
      },
    },
  },
  {
    key: 'approach',
    label: 'Approach / Fairway',
    short: 'Bad Shot',
    states: {
      resistance: {
        image: IMG.approachR,
        sub: 'The Spiral of Resistance',
        headline: 'A poor shot becomes a mental spiral.',
        body: 'The golfer leaves the present moment and becomes trapped in past failures, future consequences, and emotional resistance. The shot is over — but the story keeps playing. Tension builds, attention narrows, and the next shot inherits the storm from the last one.',
        markers: [
          'Replaying the bad shot in the head',
          'Spiralling into "what this means" for the round',
          'Fighting the emotion — trying to push it away',
          'Losing contact with the target, the routine, the now',
        ],
      },
      acceptance: {
        image: IMG.approachA,
        sub: 'The Clear Fairway',
        headline: 'A poor shot happens — and it is not amplified.',
        body: 'The golfer allows the event, notices the thoughts, releases the emotional charge, and moves freely toward the next shot. The shot ended. The story ends with it. Attention returns to what is in front of you — the lie, the target, the commitment.',
        markers: [
          'Noticing the thought without becoming the thought',
          'Letting the emotion rise and pass on its own',
          'Returning cleanly to the present shot',
          'Choosing the next target with a full, free swing',
        ],
      },
    },
  },
  {
    key: 'short-game',
    label: 'Short Game',
    short: 'Touch',
    states: {
      resistance: {
        image: IMG.shortGameR,
        sub: 'Tentative Hands',
        headline: 'Around the green, hesitation kills the shot.',
        body: 'The ball is sitting close to the hole — and that proximity creates pressure. Doubt about contact, fear of fluffing or blading, and the urge to be "too careful" steals commitment from the stroke. The hands tighten, the swing becomes tentative, the result confirms the fear.',
        markers: [
          'Tinkering with technique mid set-up',
          'Trying to be perfect rather than committed',
          'Steering the club instead of swinging it',
          'Trying to avoid the bad shot rather than play the good one',
        ],
      },
      acceptance: {
        image: IMG.shortGameA,
        sub: 'Soft & Committed',
        headline: 'Touch comes from trust, not control.',
        body: 'You cannot grip your way to feel. Acceptance allows the doubt to exist and still lets the hands stay soft. Commit to the picture, trust the technique you have practised, and release the club through the ball — outcome welcome, whatever it is.',
        markers: [
          'Allowing tension and softening through it',
          'Picking a landing spot and trusting it',
          'Releasing the club rather than guiding it',
          'Walking forward with the same posture whatever the result',
        ],
      },
    },
  },
  {
    key: 'putting',
    label: 'Putting',
    short: 'Stroke',
    states: {
      resistance: {
        image: IMG.puttingR,
        sub: 'Doubt on the Green',
        headline: 'On the green, the smallest doubt becomes the biggest miss.',
        body: 'The shorter the putt, the louder the noise. Your mind reads the line three different ways, the body second-guesses the stroke, and the desire to make it strangles the freedom needed to roll it. Pressure compresses time and shrinks the hole.',
        markers: [
          'Standing over the ball waiting for "the right feeling"',
          'Re-reading the line until commitment evaporates',
          'Steering the putter instead of stroking it',
          'Living in the consequence of missing instead of the act of rolling',
        ],
      },
      acceptance: {
        image: IMG.puttingA,
        sub: 'Read · Trust · Roll',
        headline: 'A putt is a stroke, not a verdict.',
        body: 'You read it, you trust it, you roll it. Acceptance on the green means committing to your line and your speed and being willing for the ball to do whatever it does. The make is welcome — and so is the miss.',
        markers: [
          'Choosing your line and shutting the door on doubt',
          'Setting your speed, then your stroke, then your release',
          'Letting the ball go where it goes',
          'Walking off the green the same way regardless of result',
        ],
      },
    },
  },
  {
    key: 'final-hole',
    label: 'Final Hole',
    short: 'Stakes',
    states: {
      resistance: {
        image: IMG.finalR,
        sub: 'Outcome Hijack',
        headline: 'Drama — and the round is suddenly about everything but the next shot.',
        body: 'The leaderboard, the score you "should" shoot, the imagined celebration or disaster — none of it is happening yet, but all of it is in the swing. Outcome attachment turns the simplest shot into a test of who you are.',
        markers: [
          'Scoreboarding instead of seeing the target',
          'Trying to protect a number rather than play golf',
          'Speeding up the routine to escape the discomfort',
          'Living in the future of the shot, not the present of it',
        ],
      },
      acceptance: {
        image: IMG.finalA,
        sub: 'Just the Next Shot',
        headline: 'The last hole is just the next shot — nothing more.',
        body: 'Acceptance means letting the moment matter without letting it grow bigger than it is. You willingly accept any outcome, take a full breath, run the same routine you trust, and play the shot in front of you.',
        markers: [
          'Acknowledging the stakes, then setting them down',
          'Returning attention to grip, target, breath',
          'Committing fully — no half-swing, no half-mind',
          'Walking off having played YOUR golf, whatever the score',
        ],
      },
    },
  },
  {
    key: 'reflection',
    label: 'Post-Round Reflection',
    short: 'Reflect',
    states: {
      resistance: {
        image: IMG.reflectionR,
        sub: 'Stuck in the Storm',
        headline: 'After the round, the round keeps playing.',
        body: 'You sit with the scorecard heavy in your hand and the same loops keep running — practice more, train harder, see another coach, fix the swing, fix the mind. The reflection is not learning, it is rumination dressed up as analysis. Every replay feeds the doubt instead of the lesson.',
        markers: [
          'Replaying every miss instead of reading the round',
          'Bargaining with practice ("if I just hit 500 more balls…")',
          'Searching for a fix instead of meeting the moment',
          'Defining yourself by today\'s score',
        ],
      },
      acceptance: {
        image: IMG.reflectionA,
        sub: 'Walking Off With Wisdom',
        headline: 'The round is over. The learning walks with you.',
        body: 'Acceptance after the round means honouring what happened — the good, the ugly, the lucky, the lost — without arguing with it. You ask cleaner questions: What did I do well? What can I take forward? Did I stay true to my values regardless of the score? The card closes. The lesson stays.',
        markers: [
          'Acknowledging the score without identifying with it',
          'Naming one thing handled well and one thing to grow',
          'Separating performance from self-worth',
          'Walking forward — not stuck on 18, ready for the next round',
        ],
      },
    },
  },
  {
    key: 'away-from-golf',
    label: 'Away from Golf',
    short: 'Identity',
    states: {
      resistance: {
        image: IMG.awayR,
        sub: 'Golf Is Who I Am',
        headline: 'When the round ends, the round is still you.',
        body: 'Away from the course, the swing keeps swinging in your head. The bad shots become evidence of who you are. Your worth rises and falls with your handicap. Family, work, sleep — every part of life is colonised by golf because golf has stopped being something you do and started being something you are.',
        markers: [
          'Replaying the round long after the round is over',
          'Letting the score decide the kind of evening you have',
          '"If I play badly, what does that say about me?"',
          'Treating every miss as a verdict on your character',
        ],
      },
      acceptance: {
        image: IMG.awayA,
        sub: 'Golf Is What I Do, Not Who I Am',
        headline: 'You are more than your last round — and more than your next one.',
        body: 'Acceptance off the course means knowing the difference between what you do and who you are. Golf is a chosen pursuit, not an identity. You can care deeply about it, train hard for it, love it — and still walk away from a bad day with your sense of self intact.',
        markers: [
          '"Thoughts about the round are thoughts, not facts"',
          'Caring deeply without being defined by performance',
          'Bringing your full self to family, work, life — not just the golfer',
          'Letting today\'s round be a part of you, not the whole of you',
        ],
      },
    },
  },
];

const STEPS = [
  {
    icon: Eye,
    title: '1. Notice',
    text: 'Name what is here without judgement. "I am having the thought that I ruined my round." Giving the thought a label creates instant psychological distance — you are not the thought, you are the one who noticed it.',
  },
  {
    icon: Wind,
    title: '2. Allow',
    text: 'Let the feeling exist for a full breath. Do not argue with it, fix it, or push it away. Tension rises, peaks, and passes on its own when you stop feeding it. Acceptance is not agreement — it is permission.',
  },
  {
    icon: Anchor,
    title: '3. Return',
    text: 'Anchor back to the present shot. Feel your grip, see your target, commit to the process. The next shot is the only shot that exists. Acceptance clears the runway so you can land there fully.',
  },
];

const MACAcceptanceVsResistancePage = () => {
  const [scenarioKey, setScenarioKey] = useState('training');
  const [active, setActive] = useState('resistance');
  const [choice, setChoice] = useState(null); // 'resistance' | 'acceptance' | null
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const scenario = SCENARIOS.find((s) => s.key === scenarioKey) || SCENARIOS[0];
  const stateContent = scenario.states[active];
  const stateMeta = active === 'resistance' ? RESIST : ACCEPT;

  // Close modals on Esc + lock body scroll while a modal is open
  React.useEffect(() => {
    const anyOpen = lightboxOpen || !!choice;
    if (!anyOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
        setChoice(null);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxOpen, choice]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="acceptance-vs-resistance-page">
      <Header />

      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="relative bg-[#1A2E1D] text-white py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
          <div className="container mx-auto px-6 md:px-12 max-w-5xl relative z-10">
            <Link to="/mac/accepting" className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors text-sm" data-testid="back-to-accepting">
              <ArrowLeft className="w-4 h-4" /> Back to Step Up — Accepting
            </Link>
            <p className="text-white/50 text-sm uppercase tracking-[0.25em] mb-3">QMG Blueprint · Acceptance</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.05] mb-6">
              Acceptance<br />
              <span className="text-[#BC562B]">vs. Resistance</span>
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-3xl">
              Two golfers face the same bad shot. One gets trapped in a spiral of worry that poisons the rest of the round. The other allows the moment to exist, releases it, and walks freely to the next shot. The difference is not talent — it is a trainable mental skill.
            </p>
            <div className="mt-8">
              <QMGSymbols active="acceptance" tone="light" size="md" />
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-6xl">
          {/* Intro */}
          <section className="py-12 md:py-16" data-testid="intro-section">
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-10">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-4">The Same Shot, Two Different Minds</h2>
              <p className="text-[#57534E] leading-relaxed mb-4">
                Every golfer hits poor shots. What separates the scratch handicap from the 15-handicap is not the quality of their worst shot — it is <strong>what happens in the 30 seconds after it</strong>. Resistance amplifies pain. Acceptance dissolves it.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                Pick a moment in your round or day below and toggle between the two states. Notice what your own mind does more often — and remember, whichever one you feed grows stronger.
              </p>
            </div>
          </section>

          {/* Toggle */}
          <section className="pb-12" data-testid="state-toggle-section">
            {/* Scenario tabs */}
            <div className="mb-6" data-testid="scenario-tabs">
              <p className="text-center text-xs uppercase tracking-[0.25em] text-[#737373] mb-4">Choose a moment</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-1.5 md:gap-2">
                {SCENARIOS.map((s) => {
                  const isActive = scenarioKey === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setScenarioKey(s.key)}
                      className={`px-2 py-2.5 text-[10px] md:text-[11px] font-semibold uppercase tracking-wider border transition-all leading-tight text-center ${
                        isActive
                          ? 'bg-[#1A2E1D] text-white border-[#1A2E1D] shadow-sm'
                          : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#1A2E1D]/40 hover:text-[#1A1A1A]'
                      }`}
                      data-testid={`scenario-tab-${s.key}`}
                      role="tab"
                      aria-selected={isActive}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Acceptance / Non-Acceptance pill toggle */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex bg-white border border-[#E7E5E4] p-1.5 rounded-full" role="tablist">
                {['resistance', 'acceptance'].map((key) => {
                  const meta = key === 'resistance' ? RESIST : ACCEPT;
                  const isActive = active === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setActive(key)}
                      className={`px-6 md:px-8 py-3 rounded-full text-sm font-semibold uppercase tracking-wider transition-all ${
                        isActive
                          ? 'bg-[#2C5530] text-white shadow-sm'
                          : 'text-[#57534E] hover:text-[#1A1A1A]'
                      }`}
                      data-testid={`toggle-${key}`}
                      role="tab"
                      aria-selected={isActive}
                    >
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* State Card + Choice CTA — side-by-side on desktop */}
            <div className="grid lg:grid-cols-[1fr_320px] gap-6">
              {/* State Card */}
              <div
                className={`${stateMeta.bg} border ${stateMeta.border} overflow-hidden transition-all duration-300`}
                data-testid={`state-card-${active}`}
              >
                <div className="grid md:grid-cols-2 gap-0">
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    className="group relative md:min-h-[480px] overflow-hidden bg-[#0E1A10] flex items-center justify-center p-4 cursor-zoom-in w-full"
                    aria-label={`Enlarge ${scenario.label} ${stateMeta.label} image`}
                    data-testid="state-image-trigger"
                  >
                    <img
                      src={stateContent.image}
                      alt={`${scenario.label} — ${stateMeta.label}`}
                      className="w-full h-auto max-h-[560px] object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                      data-testid={`state-image-${active}`}
                      key={`${scenario.key}-${active}`}
                    />
                    <div className="absolute top-4 left-4 flex flex-col gap-2 items-start pointer-events-none">
                      <span
                        className="inline-block text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm"
                        style={{ backgroundColor: stateMeta.accent }}
                      >
                        {stateMeta.label}
                      </span>
                      <span className="inline-block bg-white/90 text-[#1A1A1A] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm">
                        {scenario.label}
                      </span>
                    </div>
                    <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 bg-white/90 text-[#1A1A1A] text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-sm opacity-90 group-hover:opacity-100 group-hover:bg-white transition-all pointer-events-none">
                      <ZoomIn className="w-3.5 h-3.5" />
                      Click to enlarge
                    </span>
                  </button>
                  <div className="p-8 md:p-10 flex flex-col justify-center">
                    <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: stateMeta.accent }}>
                      {stateContent.sub}
                    </p>
                    <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-5 leading-snug">
                      {stateContent.headline}
                    </h3>
                    <p className="text-[#57534E] leading-relaxed mb-6">
                      {stateContent.body}
                    </p>
                    <ul className="space-y-3">
                      {stateContent.markers.map((m, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#1A1A1A]">
                          <span
                            className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: stateMeta.accent }}
                          />
                          <span className="text-sm md:text-base leading-relaxed">{m}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Choice CTA — side panel */}
              <aside
                className="bg-[#1A2E1D] text-white p-6 lg:p-7 lg:sticky lg:top-28 lg:self-start lg:max-h-[calc(100vh-8rem)] flex flex-col"
                data-testid="choose-golfer-cta"
              >
                <div className="inline-flex items-center gap-2 self-start bg-[#BC562B] text-white text-[10px] font-bold uppercase tracking-[0.25em] px-2.5 py-1 mb-4 animate-pulse">
                  <span className="w-1.5 h-1.5 bg-white rounded-full" />
                  Your Turn
                </div>
                <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Be Honest With Yourself</p>
                <h3 className="font-['Playfair_Display'] text-2xl lg:text-[28px] leading-tight mb-3 text-white">
                  Which golfer do you want to be?
                </h3>
                <p className="text-white/75 text-sm leading-relaxed mb-6">
                  In <strong className="text-white">{scenario.label}</strong>, both states are real choices the mind makes. Pick yours.
                </p>
                <div className="flex flex-col gap-3 mt-auto">
                  <button
                    onClick={() => { setChoice('acceptance'); setActive('acceptance'); }}
                    className="group w-full inline-flex items-center justify-between gap-2 bg-[#2C5530] text-white px-5 py-4 text-xs font-semibold uppercase tracking-wider hover:bg-[#3D6B41] transition-all hover:translate-x-0.5"
                    data-testid="choose-acceptance-btn"
                  >
                    <span className="text-left leading-tight">I want to be the<br /><span className="text-sm">Acceptance golfer</span></span>
                    <ArrowRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    onClick={() => { setChoice('resistance'); setActive('resistance'); }}
                    className="group w-full inline-flex items-center justify-between gap-2 border border-white/30 text-white/85 px-5 py-4 text-xs font-semibold uppercase tracking-wider hover:bg-white/5 hover:border-white/60 transition-all"
                    data-testid="choose-resistance-btn"
                  >
                    <span className="text-left leading-tight">I am the<br /><span className="text-sm">Non-Acceptance golfer</span></span>
                    <ArrowRight className="w-4 h-4 flex-shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </button>
                </div>
                <p className="text-white/50 text-[11px] mt-5 leading-relaxed">
                  Your choice opens a quick path to talk it through with Scott — no commitment.
                </p>
              </aside>
            </div>
          </section>

          {/* 3 Steps */}
          <section className="py-12 md:py-16" data-testid="steps-section">
            <div className="max-w-2xl mb-10">
              <p className="text-[#BC562B] text-sm uppercase tracking-[0.25em] mb-3">Practice</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-4">
                3 Steps to Practise Acceptance
              </h2>
              <p className="text-[#57534E] leading-relaxed">
                Acceptance is a skill, not a mood. Like any skill it is trained one rep at a time. Use these three steps on the range, on the course, and anywhere life puts pressure on you.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {STEPS.map((step, i) => (
                <div
                  key={i}
                  className="bg-white border border-[#E7E5E4] p-6 md:p-8 hover:border-[#2C5530]/50 hover:shadow-sm transition-all"
                  data-testid={`step-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#2C5530]/10 text-[#2C5530] flex items-center justify-center mb-5">
                    <step.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-3">{step.title}</h3>
                  <p className="text-[#57534E] leading-relaxed text-sm">{step.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Paradox Quote */}
          <section className="pb-12 md:pb-16" data-testid="paradox-section">
            <div className="bg-[#2C5530] text-white p-8 md:p-12">
              <p className="text-white/60 text-xs uppercase tracking-[0.25em] mb-4">The Acceptance Paradox</p>
              <blockquote className="font-['Playfair_Display'] text-2xl md:text-3xl text-white leading-snug mb-4">
                "When you stop trying to get rid of the feeling, the feeling stops running the round."
              </blockquote>
              <p className="text-white/70 text-sm">Resistance feeds the wolf of worry. Acceptance feeds the wolf of freedom. Whichever one you practise grows.</p>
            </div>
          </section>

          {/* CTA */}
          <section className="pb-12" data-testid="cta-section">
            <div className="bg-white border border-[#E7E5E4] p-8 md:p-12 text-center">
              <div className="w-14 h-14 rounded-full bg-[#BC562B]/10 text-[#BC562B] flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-3">
                Ready to Train Acceptance With a Coach?
              </h2>
              <p className="text-[#57534E] leading-relaxed max-w-xl mx-auto mb-8">
                A 1:1 session will help you spot your own resistance patterns and build acceptance reps that hold up under tournament pressure.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/booking"
                  className="inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors"
                  data-testid="cta-book-session"
                >
                  <Calendar className="w-4 h-4" />
                  Book a Session
                </Link>
                <Link
                  to="/mac/commitment"
                  className="inline-flex items-center justify-center gap-2 border border-[#2C5530] text-[#2C5530] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#2C5530] hover:text-white transition-colors"
                  data-testid="cta-next-step"
                >
                  Next Step — Action
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />

      {/* Image Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-sm animate-in fade-in"
          onClick={() => setLightboxOpen(false)}
          data-testid="image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged scenario image"
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-colors"
            aria-label="Close image"
            data-testid="close-lightbox-btn"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="absolute top-4 left-4 md:top-6 md:left-6 flex flex-wrap gap-2 z-10">
            <span
              className="inline-block text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm"
              style={{ backgroundColor: stateMeta.accent }}
            >
              {stateMeta.label}
            </span>
            <span className="inline-block bg-white/90 text-[#1A1A1A] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-sm self-center">
              {scenario.label}
            </span>
          </div>
          <img
            src={stateContent.image}
            alt={`${scenario.label} — ${stateMeta.label}`}
            className="max-w-full max-h-[90vh] object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
        </div>
      )}

      {/* Choice Modal */}
      {choice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in"
          onClick={() => setChoice(null)}
          data-testid="choice-modal"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative bg-white max-w-lg w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setChoice(null)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#57534E] hover:text-[#1A1A1A] flex items-center justify-center transition-colors"
              aria-label="Close"
              data-testid="close-modal-btn"
            >
              <X className="w-5 h-5" />
            </button>

            <div
              className="p-8 md:p-10 text-white"
              style={{ backgroundColor: choice === 'acceptance' ? '#2C5530' : '#8B1E1E' }}
            >
              <p className="text-white/70 text-xs uppercase tracking-[0.25em] mb-3">
                {scenario.label} · {choice === 'acceptance' ? 'Acceptance' : 'Non-Acceptance'}
              </p>
              <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl leading-tight text-white">
                {choice === 'acceptance'
                  ? 'A great choice — and a trainable one.'
                  : 'You are not alone — and you do not have to stay there.'}
              </h3>
            </div>

            <div className="p-8 md:p-10">
              <p className="text-[#57534E] leading-relaxed mb-4">
                {choice === 'acceptance'
                  ? 'Acceptance is not a personality trait — it is a skill that can be coached. With the right framework you can practise it the same way you practise your swing, and bring it to your most pressured moments on the course.'
                  : 'Most golfers spend years stuck in the spiral, hoping the next drill, the next coach, or the next swing change will fix it. The work is not on your swing — it is on the relationship between you and the moment. That can be coached.'}
              </p>
              <p className="text-[#1A1A1A] font-semibold mb-6">
                Want to talk through how to start? Get in touch with Scott — no commitment, just a conversation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/booking"
                  onClick={() => setChoice(null)}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2C5530] text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors"
                  data-testid="modal-book-btn"
                >
                  <Calendar className="w-4 h-4" />
                  Book a Session
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setChoice(null)}
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#2C5530] text-[#2C5530] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-[#2C5530] hover:text-white transition-colors"
                  data-testid="modal-contact-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  Send a Message
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MACAcceptanceVsResistancePage;
